import {useCallback} from 'react';

import type {Conversation} from '@stripe-internal/conversations-client';

import {ConversationTopBar} from '../../../StripeAssistant/support/components/ActiveTopBar';
import {LiveChatTopBarContent} from '../../../StripeAssistant/support/components/LiveChatTopBar';
import {
  RACCancelFn,
  useRACDetailsForEnvironment,
} from '../../../StripeAssistant/support/hooks/useRACDetails';
import {RACTopBarContent} from '../../../StripeAssistant/support/components/RACTopBar/RACTopBar';
import type {ScreenshareRequestAction} from '../../../StripeAssistant/support/components/ConversationScreenshareButton';
import {useConversationMessageSoundsForEnvironment} from '../../../StripeAssistant/support/hooks/useConversationMessageSounds';
import {
  useThreadsApi,
  useThreadsApp,
  useThreadsConversationsController,
  useThreadsEntryPoint,
  useThreadsParentMessageBus,
  useThreadsVisibility,
  useThreadsVisitor,
} from '../../app/hooks';
import {useUnseenConversationMessagesForEnvironment} from '../../../StripeAssistant/support/hooks/useUnseenConversationMessages';

function useThreadsScreenshareRequestAction(): ScreenshareRequestAction {
  const app = useThreadsApp();

  return useCallback<ScreenshareRequestAction>(
    (name, data = {}, waitForResponse = false) => {
      return app.requestAction({
        action: {
          name,
          data,
        },
        waitForResponse,
      });
    },
    [app],
  );
}

function ThreadsLiveChatTopBar({conversation}: {conversation: Conversation}) {
  const app = useThreadsApp();
  const entryPoint = useThreadsEntryPoint();
  const requestAction = useThreadsScreenshareRequestAction();
  const onClose = useCallback(() => {
    app.close();
  }, [app]);

  return (
    <LiveChatTopBarContent
      conversation={conversation}
      onClose={onClose}
      requestAction={requestAction}
      entryPoint={entryPoint}
    />
  );
}

function ThreadsRACTopBar({conversation}: {conversation: Conversation}) {
  const app = useThreadsApp();
  const api = useThreadsApi();
  const conversations = useThreadsConversationsController();
  const entryPoint = useThreadsEntryPoint();
  const visitor = useThreadsVisitor();
  const requestAction = useThreadsScreenshareRequestAction();
  const cancelRequestACallOverride = useCallback<RACCancelFn>(
    async (_entryPoint, conversationId) => {
      const response = await (app.runtime === 'legacy'
        ? api.endConversation(conversationId)
        : conversations.end(conversationId));
      if (response.type === 'success') {
        return {type: 'success'};
      }
      if (response.code === 409) {
        return {type: 'conflict'};
      }
      if (response.code === 404) {
        return {type: 'not-found'};
      }
      throw new Error(response.error);
    },
    [app, api, conversations],
  );
  const racDetails = useRACDetailsForEnvironment(conversation, {
    api,
    entryPoint,
    visitor,
    cancelRequestACallOverride,
  });

  return (
    <RACTopBarContent
      conversation={conversation}
      racDetails={racDetails}
      requestAction={requestAction}
    />
  );
}

function ThreadsConversationTopBar({
  conversation,
}: {
  conversation: Conversation;
}) {
  const {visibility} = useThreadsVisibility();
  const parentMessageBus = useThreadsParentMessageBus();

  useUnseenConversationMessagesForEnvironment({
    conversation,
    parentMessageBus,
    visibility,
  });
  useConversationMessageSoundsForEnvironment({
    conversation,
    parentMessageBus,
    visibility,
  });

  return (
    <ConversationTopBar
      conversation={conversation}
      visibility={visibility}
      LiveChatTopBarComponent={ThreadsLiveChatTopBar}
      RACTopBarComponent={ThreadsRACTopBar}
    />
  );
}

export function ThreadsActiveTopBar({
  conversation,
}: {
  conversation?: Conversation | null;
}) {
  if (!conversation) {
    return null;
  }

  return <ThreadsConversationTopBar conversation={conversation} />;
}
